import { ClickAwayListener, Popper } from '@nexelem/deprecated-theme';
import { CheckboxList, RadioList } from '@nexelem/lists';
import { get, isEmpty, merge } from 'lodash';
import { cloneElement, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useFiltersContext } from '../../hooks';
import { FilterConfigWithValue, FilterValue, RegisterFunction } from '../../types/filter.types';
import { Button, Skeleton } from '@nexelem/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import './InternalSelect.css';

type HeaderListProps = {
  readonly onClear: () => void;
  readonly onChangeType: (isMultiple: boolean) => void;
  readonly value: string;
  readonly onChange: (value: string) => void;
};

// TBD: we can add here accessors for key if we provide options to mapping only if it necessary
export type InternalSelectProps = {
  readonly path: string;
  readonly isMultiple: boolean;
  readonly isOpen: boolean;
  readonly element: HTMLElement;
  readonly renderOptionLabel?: (value: FilterValue) => ReactNode;
  readonly getOptionId?: (option: FilterValue) => string | number;
  readonly fetchData?: (searchValue: string, filter?: FilterConfigWithValue) => Promise<FilterValue[]>;
  readonly options?: FilterValue[];
  /***
   * for registering filter in context
   */
  readonly register: RegisterFunction;
  /***
   * onClear and onChangeType will be added by ChipFilter and you can clear filter state by this function if necessary
   */
  readonly HeaderList?: ReactElement<HeaderListProps> | null;
  onClose(): void;
  readonly zIndex?: string;
};

const getOptionLabel = (option: FilterValue<string | number>) => option.key?.toString() ?? 'DEFAULT KEY';

export const InternalSelect = ({
  path,
  register,
  fetchData,
  isMultiple,
  isOpen,
  onClose,
  element,
  options,
  HeaderList,
  renderOptionLabel,
  zIndex = '100',
  getOptionId,
}: InternalSelectProps) => {
  type Created = typeof isMultiple extends true ? Array<FilterValue<string | number>> : FilterValue<string | number>;

  type FilterChip = FilterConfigWithValue<Created>;
  const { t } = useTranslation();
  const { filters, onChange } = useFiltersContext();
  const filterValue = get(filters, path, {
    isMultiple,
    value: (isMultiple ? [] : {}) as FilterChip,
    path,
  }) as FilterChip;
  const [searchValue, setSearchValue] = useState('');

  const { data, isLoading, fetchStatus } = useQuery({
    queryKey: [path, 'query', searchValue],
    queryFn: () => (fetchData ? fetchData(searchValue, filterValue) : Promise.resolve(options || [])),
    enabled: isOpen,
  });

  const parsedData = useMemo(() => {
    if (data && Array.isArray(filterValue.value) && !isEmpty(filterValue.value)) {
      const value = filterValue.value;
      return data.map((item) => merge(value.find((_) => _.key === item.key) || {}, item));
    }
    return data;
    // we need merge init objects only on init state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    register({
      path,
      isMultiple,
      value: isMultiple ? [] : { value: '', key: path },
    });
    // we don't need watch to isMultiple
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, register]);

  const onClear = useCallback(() => {
    onChange(path, { value: filterValue.isMultiple ? [] : undefined });
  }, [onChange, path, filterValue.isMultiple]);

  const onChangeType = useCallback(
    (isMultiple: boolean) => {
      onChange(path, { value: isMultiple ? [] : undefined, isMultiple });
    },
    [onChange, path],
  );

  const handleSingleValueChange = useCallback(
    (value: FilterValue | undefined) => {
      onChange(path, { value, isMultiple: false });
    },
    [path, onChange],
  );

  const handleMultipleValueChange = useCallback(
    (value: FilterValue[]) => {
      onChange(path, { value, isMultiple: true });
    },
    [onChange, path],
  );

  const ExtenderHeaderList = useMemo(
    () =>
      HeaderList
        ? cloneElement(HeaderList, {
            onClear,
            onChangeType,
            value: searchValue,
            onChange: setSearchValue,
          })
        : null,
    [searchValue, HeaderList, onChangeType, onClear],
  );

  return (
    <Popper
      anchorEl={element}
      className="mt-2 bg-surface-primary shadow-medium"
      open={isOpen}
      placement="bottom-start"
      style={{ zIndex }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <div className="internal-select-container">
          {!!HeaderList && <div className="border-b border-border-primary p-4">{ExtenderHeaderList}</div>}
          {isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div className="mt-2" key={i}>
                <Skeleton className="h-11" />
              </div>
            ))}
          {!isLoading && fetchStatus === 'idle' && (
            <>
              {filterValue.isMultiple && (
                <>
                  <CheckboxList<FilterValue<string | number>>
                    getOptionId={getOptionId}
                    getOptionLabel={getOptionLabel}
                    options={parsedData ?? []}
                    renderLabel={renderOptionLabel}
                    value={Array.isArray(filterValue.value) ? filterValue.value : [filterValue.value]}
                    onChange={handleMultipleValueChange}
                  />
                  {Array.isArray(filterValue.value) && filterValue.value.length > 0 && (
                    <Button
                      fullWidth
                      className="rounded-none shadow-top"
                      StartIcon={<FontAwesomeIcon icon={faXmark} />}
                      variant="text"
                      onClick={onClear}
                    >
                      {t('components.filters.uncheckAll')}
                    </Button>
                  )}
                </>
              )}
              {!filterValue.isMultiple && (
                <RadioList<FilterValue<string | number>>
                  getOptionId={getOptionId}
                  getOptionLabel={getOptionLabel}
                  options={parsedData ?? []}
                  renderLabel={renderOptionLabel}
                  value={filterValue.value}
                  onChange={handleSingleValueChange}
                />
              )}
            </>
          )}
        </div>
      </ClickAwayListener>
    </Popper>
  );
};
